// Participation per Σημείο/location, day by day — the response-rate sheet Aquila keeps by hand:
// locations down the side, one column per day of the selected period, responses counted in each
// cell, then Total, the expected headcount (ΑΤΟΜΑ, editable by the owner, stored server-side) and
// the completion percentage. Exports to CSV/Excel in the same layout.
import { default as React } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { t } from 'i18next';
import { withTranslation } from 'react-i18next';
import { CallApi } from 'utils/Request';
import { downloadXlsx } from 'utils/xlsx';
import Toast from 'views/Toast/Toast';
import { MONO } from "./ReportViz";

const MAX_DAYS = 92; // one season; beyond that the table (and the sheet it mimics) stops being readable
const NO_LOC = '—';

class Participation extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            targets: {},   // location -> number (as stored)
            edited: {},    // location -> string (unsaved editor values)
            dirty: false,
        };
    }

    componentDidMount() {
        if (this.props.onRef) this.props.onRef(this);
        this.loadTargets();
    }

    isOwnerEditor = () => {
        const p = this.props.user.profile;
        return !p || !p.sub_role || p.sub_role === 'full';
    }

    loadTargets = () => {
        CallApi({ url: `user/location_targets?survey_id=${this.props.survey_id}`, method: 'get' }).then(res => {
            if (res.success) {
                const targets = {};
                (res.data || []).forEach(r => { targets[r.location] = Number(r.target); });
                this.setState({ targets, edited: {}, dirty: false });
            }
        }).catch(() => { /* targets are an extra — the counts must render without them */ });
    }

    saveTargets = (rows) => {
        const targets = rows
            .filter(r => r.location !== NO_LOC)
            .map(r => ({ location: r.location, target: parseInt(this.editorValue(r.location), 10) || 0 }))
            .filter(r => r.target > 0);
        CallApi({ url: 'user/location_targets', method: 'post', data: { survey_id: this.props.survey_id, targets } }).then(res => {
            if (res.success) { Toast(200, t('Targets saved')); this.loadTargets(); }
            else Toast(400, res.error);
        });
    }

    editorValue = (loc) => {
        if (this.state.edited[loc] !== undefined) return this.state.edited[loc];
        return this.state.targets[loc] !== undefined ? String(this.state.targets[loc]) : '';
    }

    // The whole matrix, computed from the period-1 filtered responses the container hands over.
    buildMatrix = () => {
        const { surveyResponses, filterData } = this.props;
        const from = moment(filterData.from1).startOf('day');
        const to = moment(filterData.to1).startOf('day');
        let days = [];
        const d = from.clone();
        while (d.valueOf() <= to.valueOf() && days.length < MAX_DAYS) { days.push(d.clone()); d.add(1, 'day'); }
        const truncated = d.valueOf() <= to.valueOf();

        const counts = {}; // loc -> { dayKey -> n, total }
        (surveyResponses || []).forEach(r => {
            const loc = (r.location || '').trim() || NO_LOC;
            const key = moment(r.created_at).format('YYYY-MM-DD');
            if (!counts[loc]) counts[loc] = { total: 0 };
            counts[loc][key] = (counts[loc][key] || 0) + 1;
            counts[loc].total++;
        });
        // every location that answered OR has a target gets a row; NO_LOC sinks to the bottom
        const locs = Object.keys(counts).concat(Object.keys(this.state.targets).filter(l => !counts[l]));
        locs.sort((a, b) => (a === NO_LOC) - (b === NO_LOC) || String(a).localeCompare(String(b), 'el', { numeric: true }));
        const rows = locs.map(loc => {
            const c = counts[loc] || { total: 0 };
            const target = this.state.targets[loc];
            return {
                location: loc,
                perDay: days.map(day => c[day.format('YYYY-MM-DD')] || 0),
                total: c.total,
                target: target !== undefined ? target : null,
                pct: target > 0 ? c.total / target * 100 : null,
            };
        });
        const totals = {
            perDay: days.map((_, i) => rows.reduce((a, r) => a + r.perDay[i], 0)),
            total: rows.reduce((a, r) => a + r.total, 0),
            target: rows.reduce((a, r) => a + (r.target || 0), 0),
        };
        totals.pct = totals.target > 0 ? totals.total / totals.target * 100 : null;
        return { days, rows, totals, truncated };
    }

    exportRows = () => {
        const { days, rows, totals } = this.buildMatrix();
        const out = [[t('Location'), ...days.map(d => d.format('DD/MM/YYYY')), 'Total', t('People'), '%']];
        rows.forEach(r => out.push([
            r.location, ...r.perDay, r.total,
            r.target != null ? r.target : '',
            r.pct != null ? Number(r.pct.toFixed(1)) : '',
        ]));
        out.push(['Total', ...totals.perDay, totals.total, totals.target || '', totals.pct != null ? Number(totals.pct.toFixed(1)) : '']);
        return out;
    }
    generateCSV = () => {
        const rows = this.exportRows();
        const csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n");
        window.open(encodeURI(csvContent));
    }
    generateExcel = (fileName) => { downloadXlsx([{ name: 'Participation', rows: this.exportRows() }], fileName); }
    generatePdf = () => { window.print(); }

    render() {
        const { days, rows, totals, truncated } = this.buildMatrix();
        const canEdit = this.isOwnerEditor();
        const cardStyle = { background: '#fff', border: '1px solid #e2e8e4', borderRadius: 14, boxShadow: '0 1px 2px rgba(20,40,30,.06),0 6px 20px rgba(20,40,30,.05)', overflow: 'hidden' };
        const th = { padding: '8px 8px', fontSize: 11.5, fontWeight: 700, color: '#8a988f', textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: '1px solid #e2e8e4', whiteSpace: 'nowrap', textAlign: 'center', background: '#fff' };
        const td = { padding: '7px 8px', fontSize: 13, fontFamily: MONO, textAlign: 'center', borderBottom: '1px solid #eef2f0', whiteSpace: 'nowrap' };
        const sticky = { position: 'sticky', left: 0, background: '#fff', textAlign: 'left', zIndex: 1 };
        const pctColor = (p) => p == null ? '#8a988f' : (p >= 70 ? '#2f9e44' : p >= 40 ? '#e9a23b' : '#d1495b');
        return (
            <div style={cardStyle}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>📊 {t('Participation by location')}</div>
                    <div style={{ fontSize: 12.5, color: '#8a988f' }}>
                        {t('Responses per day for each location, against the expected number of people.')}
                    </div>
                </div>
                {truncated && <div style={{ padding: '8px 18px', fontSize: 12.5, color: '#b26a00', background: '#fff8e6' }}>
                    ⚠ {t('Showing the first {n} days of the selected period.').replace('{n}', MAX_DAYS)}
                </div>}
                <div style={{ overflowX: 'auto', padding: '0 0 6px' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 160 + days.length * 44 }}>
                        <thead>
                            <tr>
                                <th style={{ ...th, ...sticky, zIndex: 2 }}>{t('Location')}</th>
                                {days.map((day, i) => <th key={i} style={th} title={day.format('dddd LL')}>{day.format('D/M')}</th>)}
                                <th style={{ ...th, borderLeft: '2px solid #e2e8e4' }}>Total</th>
                                <th style={th}>{t('People')}</th>
                                <th style={th}>%</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, k) => (
                                <tr key={k} style={{ background: k % 2 ? '#fafcfb' : '#fff' }}>
                                    <td style={{ ...td, ...sticky, fontFamily: 'inherit', fontWeight: 700, background: k % 2 ? '#fafcfb' : '#fff' }}>
                                        {r.location === NO_LOC ? <span style={{ color: '#8a988f', fontWeight: 400 }}>{t('(no location)')}</span> : r.location}
                                    </td>
                                    {r.perDay.map((n, i) => <td key={i} style={{ ...td, color: n ? '#1b2320' : '#c4cdc8' }}>{n || '·'}</td>)}
                                    <td style={{ ...td, fontWeight: 700, borderLeft: '2px solid #e2e8e4' }}>{r.total}</td>
                                    <td style={td}>
                                        {canEdit && r.location !== NO_LOC
                                            ? <input type="number" min="0" value={this.editorValue(r.location)}
                                                onChange={e => this.setState({ edited: { ...this.state.edited, [r.location]: e.target.value }, dirty: true })}
                                                style={{ width: 64, fontFamily: MONO, fontSize: 13, textAlign: 'center', border: '1px solid #cfd8d3', borderRadius: 7, padding: '4px 4px' }} />
                                            : (r.target != null ? r.target : '—')}
                                    </td>
                                    <td style={{ ...td, fontWeight: 700, color: pctColor(r.pct) }}>{r.pct != null ? r.pct.toFixed(1) + '%' : '—'}</td>
                                </tr>
                            ))}
                            <tr style={{ background: '#f2f7f4' }}>
                                <td style={{ ...td, ...sticky, fontFamily: 'inherit', fontWeight: 800, background: '#f2f7f4' }}>Total</td>
                                {totals.perDay.map((n, i) => <td key={i} style={{ ...td, fontWeight: 700 }}>{n || '·'}</td>)}
                                <td style={{ ...td, fontWeight: 800, borderLeft: '2px solid #e2e8e4' }}>{totals.total}</td>
                                <td style={{ ...td, fontWeight: 700 }}>{totals.target || '—'}</td>
                                <td style={{ ...td, fontWeight: 800, color: pctColor(totals.pct) }}>{totals.pct != null ? totals.pct.toFixed(1) + '%' : '—'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                {rows.length === 0 && <div style={{ padding: 18, color: '#8a988f', fontSize: 14 }}>{t('No responses in the selected period.')}</div>}
                {canEdit && (
                    <div style={{ padding: '10px 18px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderTop: '1px solid #eef2f0' }}>
                        <button onClick={() => this.saveTargets(rows)} disabled={!this.state.dirty}
                            style={{ fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: '#fff', background: this.state.dirty ? '#2f9e44' : '#a8bfb0', border: 0, borderRadius: 9, padding: '8px 16px', cursor: this.state.dirty ? 'pointer' : 'default' }}>
                            {t('Save targets')}
                        </button>
                        <span style={{ fontSize: 12, color: '#8a988f' }}>{t('People = how many are expected to answer at each location. The percentage is Total / People.')}</span>
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = ({ user }) => ({ user });
export default connect(mapStateToProps)(withTranslation()(Participation));
